import {InfoIcon} from '@primer/octicons-react'
import {Button, Heading, Popover} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useEffect, useRef} from 'react'

import {useUserNotices} from '../../state-providers/user-notices/user-notices-provider'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {UserNoticeResources} from '../../strings'
import styles from './memex-timestamp-fields-popover.module.css'

const HEADING_ID = 'timestamp-fields-notice-heading'

export const MemexTimestampFieldsPopover = () => {
  const {userNotices, dismissUserNotice} = useUserNotices()
  const {anchorRef: viewButtonRef} = useViewOptionsMenuRef()
  const gotItButtonRef = useRef<HTMLButtonElement | null>(null)

  const onDismiss = useCallback(
    (tookAction = false) => {
      dismissUserNotice('memex_timestamp_fields_notice', tookAction)
      viewButtonRef.current?.focus()
    },
    [dismissUserNotice, viewButtonRef],
  )

  const isVisible = userNotices.memex_timestamp_fields_notice

  useEffect(() => {
    if (!isVisible) return

    // Use requestAnimationFrame to schedule focus after the browser has
    // completed layout and any sibling effects that may move focus elsewhere.
    const rafId = requestAnimationFrame(() => {
      gotItButtonRef.current?.focus()
    })
    return () => cancelAnimationFrame(rafId)
  }, [isVisible])

  if (!isVisible) return null

  return (
    <Popover
      relative={false}
      open
      caret="top-right"
      className={styles.popoverContainer}
      role="dialog"
      aria-labelledby={HEADING_ID}
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      onKeyDown={e => e.key === 'Escape' && onDismiss()}
    >
      <Popover.Content>
        <Heading as="h2" id={HEADING_ID} className={styles.popoverHeading}>
          <div className={styles.headingRow}>
            <Octicon size={14} icon={InfoIcon} className={styles.infoIcon} />
            {UserNoticeResources.TimestampFieldsPopover.title}
          </div>
        </Heading>
        <p className={styles.popoverText}>{UserNoticeResources.TimestampFieldsPopover.description}</p>
        <div className={styles.actionsRow}>
          <Button ref={gotItButtonRef} onClick={() => onDismiss(true)}>
            {UserNoticeResources.TimestampFieldsPopover.actionPrimary}
          </Button>
          <Button variant="invisible" onClick={() => onDismiss()}>
            {UserNoticeResources.TimestampFieldsPopover.actionSecondary}
          </Button>
        </div>
      </Popover.Content>
    </Popover>
  )
}
